import { useState } from "react";
import { Pencil, Plus } from "lucide-react";

import { TermChip } from "@/components/filters/TermChip";
import type { TdmKeyword } from "@/hooks/useTdmFilters";

/**
 * Tira de términos del TDM: clic en la pill la prende/apaga, la X la quita y
 * "Gestionar" abre el modal donde el clic edita el texto.
 */
export function TdmKeywordPills({
  keywords,
  onChange,
  onManage,
}: {
  keywords: TdmKeyword[];
  onChange: (next: TdmKeyword[]) => void;
  onManage: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const activeCount = keywords.filter((k) => k.active).length;

  function close() {
    setAdding(false);
    setDraft("");
  }

  // Se agrega tal cual: el espacio de borde es intencional (" asf" no matchea
  // "MASF"), mismo criterio que los términos del perfil.
  function commit() {
    if (!draft.trim()) return close();
    if (!keywords.some((k) => k.term === draft))
      onChange([...keywords, { term: draft, active: true }]);
    setDraft("");
  }

  return (
    <div className="flex min-w-[260px] flex-1 flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
          Recursos a buscar
        </span>
        <span className="rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground">
          {activeCount === keywords.length
            ? keywords.length
            : `${activeCount} de ${keywords.length}`}
        </span>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          Materiales + Subcontratos
        </span>
        <button
          type="button"
          onClick={onManage}
          className="ml-auto flex flex-none cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/50"
        >
          <Pencil className="size-3" />
          Gestionar
        </button>
      </div>

      <div className="flex flex-wrap items-start gap-1.5">
        {keywords.length === 0 && !adding && (
          <span className="py-1 text-xs italic text-muted-foreground">
            Sin términos: se busca "asf" por defecto.
          </span>
        )}
        {keywords.map((k, i) => (
          <TermChip
            key={`${k.term}-${i}`}
            term={k.term}
            active={k.active}
            onRemove={() => onChange(keywords.filter((_, idx) => idx !== i))}
            onToggle={() =>
              onChange(
                keywords.map((x, idx) =>
                  idx === i ? { ...x, active: !x.active } : x,
                ),
              )
            }
          />
        ))}

        {adding ? (
          <span className="inline-flex h-7 flex-none items-center gap-1.5 rounded-md border border-blue-500 bg-card pl-2.5 pr-1.5 ring-2 ring-blue-500/20 dark:border-blue-600">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={close}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  close();
                }
              }}
              placeholder="ej. asf"
              className="w-32 border-0 bg-transparent text-xs font-semibold outline-none placeholder:font-normal placeholder:text-muted-foreground"
            />
            <span className="text-[10px] text-muted-foreground">⏎</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-7 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-dashed px-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-700 dark:hover:text-blue-400"
          >
            <Plus className="size-3" />
            Añadir
          </button>
        )}
      </div>
    </div>
  );
}
